import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import DocumentationChecklist from './DocumentationChecklist';
import {
  generateDataStrategyFrameworkPDF,
  generateConsultationPreparationPDF,
  generateGovernanceFrameworkPDF,
  generateKeyStakeholdersPDF,
  generateSuccessMetricsPDF
} from '../../../utils/templatePdfGenerator';

const PreparationResources = ({ serviceType }) => {
  const [checkedItems, setCheckedItems] = useState({});
  const [showDocChecklist, setShowDocChecklist] = useState(false);

  const toggleCheckItem = (itemId) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev?.[itemId]
    }));
  };

  // Handle resource download
  const handleResourceDownload = (resourceTitle) => {
    switch (resourceTitle) {
      case 'Data Strategy Framework Guide':
        generateDataStrategyFrameworkPDF();
        break;
      case 'Consultation Preparation Template':
        generateConsultationPreparationPDF();
        break;
      case 'Governance Framework Template':
        generateGovernanceFrameworkPDF();
        break;
      default:
        console.log('Resource download:', resourceTitle);
    }
  };

  // Handle checklist item action
  const handleChecklistAction = (item) => {
    if (item?.id === 'documents') {
      setShowDocChecklist(true);
    } else if (item?.downloadAction === 'stakeholders') {
      generateKeyStakeholdersPDF();
    } else if (item?.downloadAction === 'metrics') {
      generateSuccessMetricsPDF();
    } else if (item?.link) {
      window.open(item?.link, '_blank');
    }
  };

  const preparationChecklist = [
    {
      id: 'questionnaire',
      title: 'Complete Pre-Consultation Questionnaire',
      description: 'Help us understand your current data landscape and strategic goals',
      icon: 'ClipboardList',
      action: 'Complete Now',
      link: '/data-maturity-calculator-lead-generation-assessment'
    },
    {
      id: 'documents',
      title: 'Gather Relevant Documentation',
      description: 'Current data architecture diagrams, tech stack overview, or pain points document',
      icon: 'FileText',
      action: 'View Checklist'
    },
    {
      id: 'stakeholders',
      title: 'Identify Key Stakeholders',
      description: 'List decision-makers and team members who should be involved',
      icon: 'Users',
      action: 'Download Guide',
      downloadAction: 'stakeholders'
    },
    {
      id: 'goals',
      title: 'Define Your Success Metrics',
      description: 'What outcomes would make this consultation valuable for your organization?',
      icon: 'Target',
      action: 'Download Guide',
      downloadAction: 'metrics'
    }
  ];

  const getResourcesByServiceType = () => {
    const baseResources = [
      {
        title: 'Data Strategy Framework Guide',
        description: 'Comprehensive guide to building a modern data strategy',
        icon: 'BookOpen',
        type: 'PDF Guide',
        color: 'bg-[#002B45]'
      },
      {
        title: 'Consultation Preparation Template',
        description: 'Structured template to organize your thoughts and questions',
        icon: 'FileSpreadsheet',
        type: 'Template',
        color: 'bg-green-500'
      }
    ];

    const serviceSpecificResources = {
      'AI Solutions': [
        {
          title: 'AI Readiness Assessment',
          description: 'Evaluate your organization\'s readiness for AI implementation',
          icon: 'Sparkles',
          type: 'Assessment',
          color: 'bg-purple-500'
        },
        {
          title: 'AI Use Case Library',
          description: 'Industry-specific AI applications and success stories',
          icon: 'Lightbulb',
          type: 'Case Studies',
          color: 'bg-yellow-500'
        }
      ],
      'Fractional CDO': [
        {
          title: 'CDO Engagement Model Overview',
          description: 'Understanding fractional leadership and engagement structures',
          icon: 'Crown',
          type: 'Overview',
          color: 'bg-[#002B45]'
        },
        {
          title: 'Executive Data Strategy Playbook',
          description: 'Strategic frameworks for C-level data leadership',
          icon: 'Briefcase',
          type: 'Playbook',
          color: 'bg-red-500'
        }
      ],
      'Data Governance': [
        {
          title: 'Governance Framework Template',
          description: 'Establish data governance policies and procedures',
          icon: 'Shield',
          type: 'Framework',
          color: 'bg-[#1FB5A5]'
        }
      ]
    };

    const specificResources = serviceSpecificResources?.[serviceType] || [];
    return [...baseResources, ...specificResources];
  };

  const resources = getResourcesByServiceType();

  const completedCount = Object.values(checkedItems)?.filter(Boolean)?.length;
  const totalCount = preparationChecklist?.length;
  const progressPercentage = (completedCount / totalCount) * 100;

  return (
    <section className="py-16 px-4 bg-white/5 backdrop-blur-sm">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Prepare for Your Consultation
          </h2>
          <p className="text-xl text-white/80">
            Maximize the value of your consultation with these preparation resources
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Preparation Checklist */}
          <div className="bg-white rounded-2xl shadow-premium p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-[#002B45]">
                Pre-Consultation Checklist
              </h3>
              <div className="text-right">
                <p className="text-sm text-gray-500">Progress</p>
                <p className="text-2xl font-bold text-[#1FB5A5]">
                  {completedCount}/{totalCount}
                </p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-[#1FB5A5] to-emerald-500 h-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>

            {/* Checklist Items */}
            <div className="space-y-4">
              {preparationChecklist?.map((item) => (
                <div
                  key={item?.id}
                  className={`border-2 rounded-xl p-4 transition-all duration-300 ${
                    checkedItems?.[item?.id]
                      ? 'border-[#1FB5A5] bg-emerald-50'
                      : 'border-gray-200 hover:border-[#1FB5A5]/50'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <button
                      onClick={() => toggleCheckItem(item?.id)}
                      className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${
                        checkedItems?.[item?.id]
                          ? 'bg-[#1FB5A5] border-[#1FB5A5]'
                          : 'border-gray-300 hover:border-[#1FB5A5]'
                      }`}
                    >
                      {checkedItems?.[item?.id] && (
                        <Icon name="Check" size={16} className="text-white" />
                      )}
                    </button>

                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Icon
                            name={item?.icon}
                            size={20}
                            className={checkedItems?.[item?.id] ? 'text-[#1FB5A5]' : 'text-gray-400'}
                          />
                          <h4
                            className={`font-semibold ${
                              checkedItems?.[item?.id] ? 'text-[#002B45] line-through' : 'text-[#002B45]'
                            }`}
                          >
                            {item?.title}
                          </h4>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{item?.description}</p>
                      {item?.action && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[#1FB5A5] border-[#1FB5A5] hover:bg-[#1FB5A5] hover:text-white"
                          onClick={() => handleChecklistAction(item)}
                        >
                          {item?.action}
                          <Icon name="ArrowRight" size={14} className="ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommended Resources */}
          <div className="bg-white rounded-2xl shadow-premium p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-[#002B45] rounded-lg flex items-center justify-center">
                <Icon name="Library" size={24} className="text-[#1FB5A5]" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#002B45]">
                  Recommended Resources
                </h3>
                <p className="text-sm text-gray-600">Curated for your consultation type</p>
              </div>
            </div>

            <div className="space-y-4">
              {resources?.map((resource, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl p-4 hover:border-[#1FB5A5] hover:shadow-elevation transition-all duration-300 cursor-pointer group"
                  onClick={() => handleResourceDownload(resource?.title)}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`${resource?.color} w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <Icon name={resource?.icon} size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-[#002B45] group-hover:text-[#1FB5A5] transition-colors">
                          {resource?.title}
                        </h4>
                        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {resource?.type}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{resource?.description}</p>
                      <div className="flex items-center space-x-2 text-[#1FB5A5] text-sm font-medium">
                        <Icon name="Download" size={14} />
                        <span>Download Resource</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Help */}
            <div className="mt-6 p-4 bg-gradient-to-r from-[#002B45] to-[#003D5C] rounded-xl">
              <div className="flex items-start space-x-3">
                <Icon name="HelpCircle" size={20} className="text-[#1FB5A5] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-white mb-1">Need Additional Resources?</p>
                  <p className="text-sm text-white/80 mb-3">
                    Contact us for industry-specific materials or custom preparation guidance.
                  </p>
                  <Button size="sm" variant="secondary">
                    Request Custom Resources
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Documentation Checklist Modal */}
      <DocumentationChecklist
        isOpen={showDocChecklist}
        onClose={() => setShowDocChecklist(false)}
        serviceType={serviceType}
      />
    </section>
  );
};

export default PreparationResources;